import { useMemo } from 'react'
import { getBaseCarcCode } from '~/entities/carc-rarc'
import type { TagInfo } from '~/shared/api/data-ingestion-api/client/types.gen'
import { CSV_EXPLANATIONS } from '../lib/csv-explanations'
import { MOCK_REASONING_DATA } from '../lib/mock-reasoning-data'
import type { OptiReasoningData, OptiSuggestion, ReasoningBlock } from '../lib/types'

function getCsvExplanation(code: string): string | undefined {
  if (CSV_EXPLANATIONS[code]) return CSV_EXPLANATIONS[code]
  const baseCode = code.replace(/^(DN|RR)/, '')
  const dnKey = `DN${baseCode}`
  if (CSV_EXPLANATIONS[dnKey]) return CSV_EXPLANATIONS[dnKey]
  return undefined
}

function parseTagInfoToSuggestion(tagInfo: TagInfo): OptiSuggestion | null {
  if (!tagInfo.evidence && !tagInfo.explanation) return null

  const quotes: string[] = []
  if (tagInfo.evidence) {
    const quoteMatches = tagInfo.evidence.match(/"([^"]+)"/g)
    if (quoteMatches) {
      quotes.push(...quoteMatches.map((q) => q.replace(/"/g, '')))
    }
  }

  const csvReasoning = getCsvExplanation(tagInfo.code)

  return {
    code: getBaseCarcCode(tagInfo.code),
    description: tagInfo.description ?? '',
    reasoning: csvReasoning ?? tagInfo.explanation ?? tagInfo.evidence ?? '',
    extractedQuotes: quotes,
    isDenied: tagInfo.evidence?.toLowerCase().includes('denied'),
  }
}

function parseTagInfoToReasoningBlocks(tagInfos: TagInfo[]): ReasoningBlock[] {
  const blocks: ReasoningBlock[] = []

  for (const tagInfo of tagInfos) {
    if (!tagInfo.explanation) continue

    const bullets = tagInfo.explanation
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('•') || line.startsWith('-'))
      .map((line) => line.replace(/^[•-]\s*/, ''))

    const quotes: string[] = []
    if (tagInfo.evidence) {
      const quoteMatches = tagInfo.evidence.match(/"([^"]+)"/g)
      if (quoteMatches) {
        quotes.push(...quoteMatches.map((q) => q.replace(/"/g, '')))
      }
    }

    if (bullets.length > 0 || quotes.length > 0) {
      blocks.push({
        tags: tagInfo.category ? [tagInfo.category] : [tagInfo.code],
        bulletPoints: bullets.length > 0 ? bullets : [tagInfo.explanation],
        extractedQuotes: quotes,
      })
    }
  }

  return blocks
}

interface UseOptiReasoningParams {
  llmFields?: Array<{ name: string; value: unknown }> | null
}

export function useOptiReasoning({ llmFields }: UseOptiReasoningParams): OptiReasoningData {
  return useMemo(() => {
    if (!llmFields) return MOCK_REASONING_DATA

    const carcField = llmFields.find((f) => f.name === 'carc_codes')
    const carcValues = (carcField?.value as TagInfo[] | undefined) ?? []

    const suggestions = carcValues
      .map(parseTagInfoToSuggestion)
      .filter((s): s is OptiSuggestion => s !== null)

    if (suggestions.length === 0) return MOCK_REASONING_DATA

    const firstCode = carcValues[0]?.code ?? ''
    let prefixLabel = ''
    if (firstCode.startsWith('DN')) prefixLabel = 'DN - Correspondence'
    else if (firstCode.startsWith('RR')) prefixLabel = 'RR - Refund Request'

    const allTagInfos = llmFields.flatMap((f) => (f.value as TagInfo[]) ?? [])
    const reasoningBlocks = parseTagInfoToReasoningBlocks(allTagInfos)

    return {
      prefixSuggestion: {
        prefix: prefixLabel,
        description: prefixLabel
          ? `opti suggests ${prefixLabel} based on the LLM-extracted codes.`
          : '',
      },
      carcSuggestions: suggestions,
      reasoningBlocks,
    }
  }, [llmFields])
}
